import { Link } from '@tanstack/react-router';
import { CalendarPlus, CheckCircle, Gift, Plus, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { AdminLayout } from '@/app/shell/admin-layout';
import {
  type ListSubscriptionsQuery,
  type PaymentProvider,
  type SubscriptionStatus,
  useAdminSubscriptions,
  useExpireSubscription,
  useExtendSubscription,
} from '@/shared/api/admin-subscriptions';
import { cn } from '@/shared/lib/cn';
import { ReasonDialog } from '@/shared/ui/reason-dialog';

const PAGE_SIZE = 50;
const STATUSES: SubscriptionStatus[] = ['free', 'trial', 'active', 'past_due', 'cancelled', 'lifetime'];
const PROVIDERS: PaymentProvider[] = ['manual', 'stripe', 'yookassa', 'lifetime'];

export function SubscriptionsListPage() {
  const [status, setStatus] = useState<SubscriptionStatus | undefined>(undefined);
  const [provider, setProvider] = useState<PaymentProvider | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [expireTarget, setExpireTarget] = useState<{ id: string; planCode: string } | null>(null);
  const [extendTarget, setExtendTarget] = useState<
    { id: string; planCode: string; currentPeriodEnd: string | null } | null
  >(null);
  const [extendDays, setExtendDays] = useState<string>('30');

  const query: ListSubscriptionsQuery = { limit: PAGE_SIZE, offset };
  if (status) query.status = status;
  if (provider) query.paymentProvider = provider;

  const { data, isLoading, isFetching } = useAdminSubscriptions(query);
  const expireMut = useExpireSubscription();
  const extendMut = useExtendSubscription();

  return (
    <AdminLayout>
      <header className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold">Subscriptions</h1>
          <p className="text-sm text-neutral-400">
            View all subscriptions.  Grant Pro / Team manually.  All mutations audit-logged.
          </p>
        </div>
        <Link
          to="/subscriptions/grant"
          className="inline-flex items-center gap-1.5 rounded-md bg-orange-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-700"
        >
          <Plus size={14} /> Grant subscription
        </Link>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select
          label="Status"
          value={status}
          options={STATUSES}
          onChange={(v) => {
            setStatus(v as SubscriptionStatus | undefined);
            setOffset(0);
          }}
        />
        <Select
          label="Provider"
          value={provider}
          options={PROVIDERS}
          onChange={(v) => {
            setProvider(v as PaymentProvider | undefined);
            setOffset(0);
          }}
        />
        <div className="ml-auto text-xs text-neutral-500">
          {isFetching ? 'Loading…' : data ? `${data.total} total` : null}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-neutral-800 bg-neutral-900">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2 font-medium">Customer</th>
              <th className="px-4 py-2 font-medium">Plan</th>
              <th className="px-4 py-2 font-medium">Status</th>
              <th className="px-4 py-2 font-medium">Provider</th>
              <th className="px-4 py-2 font-medium">Period end</th>
              <th className="px-4 py-2 font-medium">Granted by</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-800">
            {isLoading && !data ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                  Loading…
                </td>
              </tr>
            ) : data && data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-neutral-500">
                  No subscriptions match these filters.
                </td>
              </tr>
            ) : (
              data?.items.map((s) => (
                <tr key={s.id} className="hover:bg-neutral-800/40">
                  <td className="px-4 py-2 text-neutral-200">
                    <Link
                      to="/subscriptions/$id"
                      params={{ id: s.id }}
                      className="block hover:text-neutral-100"
                    >
                      <div className="text-xs uppercase text-neutral-500">{s.customerType}</div>
                      <code className="text-xs text-neutral-400">{s.customerId.slice(0, 8)}…</code>
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-neutral-100">
                    <Link
                      to="/subscriptions/$id"
                      params={{ id: s.id }}
                      className="hover:underline"
                    >
                      {s.planCode}
                    </Link>
                  </td>
                  <td className="px-4 py-2">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-4 py-2 text-neutral-400">{s.paymentProvider}</td>
                  <td className="px-4 py-2 text-neutral-400">
                    {s.currentPeriodEnd ? formatDate(s.currentPeriodEnd) : 'lifetime'}
                  </td>
                  <td className="px-4 py-2 text-neutral-500">
                    {s.grantedBy ? <code className="text-xs">{s.grantedBy.slice(0, 8)}…</code> : '—'}
                    {s.grantReason ? (
                      <div className="mt-0.5 max-w-xs truncate text-xs italic text-neutral-600" title={s.grantReason}>
                        {s.grantReason}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {(['trial', 'active'] as SubscriptionStatus[]).includes(s.status) ? (
                      <div className="flex items-center justify-end gap-2 text-xs">
                        <button
                          onClick={() =>
                            setExtendTarget({
                              id: s.id,
                              planCode: s.planCode,
                              currentPeriodEnd: s.currentPeriodEnd,
                            })
                          }
                          className="inline-flex items-center gap-1 text-orange-400 hover:text-orange-300"
                          title="Extend the period_end of this subscription"
                        >
                          <CalendarPlus size={12} /> Extend
                        </button>
                        <button
                          onClick={() => setExpireTarget({ id: s.id, planCode: s.planCode })}
                          className="text-red-400 hover:text-red-300"
                        >
                          Expire
                        </button>
                      </div>
                    ) : s.status === 'lifetime' ? (
                      <button
                        onClick={() => setExpireTarget({ id: s.id, planCode: s.planCode })}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Expire
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > PAGE_SIZE ? (
        <div className="mt-4 flex items-center justify-between text-sm text-neutral-400">
          <div>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, data.total)} of {data.total}
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              className="rounded-md bg-neutral-800 px-3 py-1.5 hover:bg-neutral-700 disabled:opacity-40"
            >
              ← Prev
            </button>
            <button
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-md bg-neutral-800 px-3 py-1.5 hover:bg-neutral-700 disabled:opacity-40"
            >
              Next →
            </button>
          </div>
        </div>
      ) : null}

      {expireTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => !o && setExpireTarget(null)}
          title="Expire subscription?"
          description={`Customer immediately downgrades to free.  Existing data is preserved (soft enforcement).  Plan was: ${expireTarget.planCode}.`}
          confirmLabel="Expire"
          tone="danger"
          pending={expireMut.isPending}
          onConfirm={async (reason) => {
            await expireMut.mutateAsync({ id: expireTarget.id, reason });
            toast.success('Subscription expired');
            setExpireTarget(null);
          }}
        />
      ) : null}

      {extendTarget ? (
        <ReasonDialog
          open={true}
          onOpenChange={(o) => {
            if (!o) {
              setExtendTarget(null);
              setExtendDays('30');
            }
          }}
          title="Extend subscription period?"
          description={`Push the period_end forward without changing plan or pricing.  Plan: ${
            extendTarget.planCode
          } · current period ends ${
            extendTarget.currentPeriodEnd
              ? new Date(extendTarget.currentPeriodEnd).toLocaleDateString()
              : 'lifetime'
          }.`}
          confirmLabel={`Extend by ${extendDays || '?'} day${extendDays === '1' ? '' : 's'}`}
          tone="default"
          pending={extendMut.isPending}
          onConfirm={async (reason) => {
            const days = Number(extendDays);
            if (!Number.isFinite(days) || days <= 0 || days > 3650) {
              toast.error('Days must be a positive integer ≤ 3650');
              throw new Error('invalid_days'); // keeps dialog open
            }
            const next = await extendMut.mutateAsync({
              id: extendTarget.id,
              additionalDays: days,
              reason,
            });
            toast.success(
              `Period extended.  New end: ${
                next.currentPeriodEnd
                  ? new Date(next.currentPeriodEnd).toLocaleDateString()
                  : 'lifetime'
              }`,
            );
            setExtendTarget(null);
            setExtendDays('30');
          }}
        >
          <label className="mb-1.5 block text-xs uppercase tracking-wider text-neutral-400">
            Additional days <span className="text-neutral-600">(1–3650)</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={3650}
              value={extendDays}
              onChange={(e) => setExtendDays(e.target.value)}
              className="w-28 rounded-md bg-neutral-800 px-3 py-2 text-sm text-neutral-100 outline-none ring-1 ring-neutral-700 focus:ring-2 focus:ring-orange-500"
            />
            <span className="text-xs text-neutral-500">
              {extendTarget.currentPeriodEnd && extendDays && Number(extendDays) > 0
                ? `→ ${new Date(
                    new Date(extendTarget.currentPeriodEnd).getTime() +
                      Number(extendDays) * 24 * 60 * 60 * 1000,
                  ).toLocaleDateString()}`
                : extendTarget.currentPeriodEnd === null
                  ? '(currently lifetime — extending a lifetime sub is a no-op)'
                  : null}
            </span>
          </div>
        </ReasonDialog>
      ) : null}
    </AdminLayout>
  );
}

function Select(props: {
  label: string;
  value: string | undefined;
  options: string[];
  onChange: (v: string | undefined) => void;
}) {
  return (
    <label className="flex items-center gap-2 rounded-md bg-neutral-900 px-2 py-1.5 text-xs ring-1 ring-neutral-800">
      <span className="text-neutral-500">{props.label}:</span>
      <select
        value={props.value ?? ''}
        onChange={(e) => props.onChange(e.target.value || undefined)}
        className="bg-transparent text-neutral-200 outline-none"
      >
        <option value="">all</option>
        {props.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

function StatusBadge({ status }: { status: SubscriptionStatus }) {
  const tone = {
    free: 'bg-neutral-800 text-neutral-400 ring-neutral-700',
    trial: 'bg-blue-500/15 text-blue-300 ring-blue-500/30',
    active: 'bg-emerald-500/15 text-emerald-300 ring-emerald-500/30',
    past_due: 'bg-amber-500/15 text-amber-300 ring-amber-500/30',
    cancelled: 'bg-neutral-700/30 text-neutral-400 ring-neutral-700',
    lifetime: 'bg-orange-500/15 text-orange-300 ring-orange-500/30',
  }[status];
  const Icon = status === 'active' || status === 'lifetime' ? CheckCircle : status === 'cancelled' ? XCircle : Gift;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1', tone)}>
      <Icon size={10} /> {status}
    </span>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}
